import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";

type UseTabParamOptions<T extends string> = {
  /** Search param holding the tab. Defaults to "tab". */
  param?: string;
  /** Extra URL values that land on a real tab (old deep links, AI widget links). */
  aliases?: Readonly<Record<string, T>>;
  /** False while a conditionally shown tab is hidden; the default tab shows instead. */
  isAvailable?: (tab: T) => boolean;
};

// Active tab of a page's static tab set, kept in the URL (the `tab-url-state`
// skill) so reloads, back-navigation and shared links land on the same tab:
//   const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, "settings");
//   <Tabs value={activeTab} onValueChange={setActiveTab}>
// The URL is the only state. An unknown or unavailable value shows the default
// without rewriting the URL, the default itself is left out of the URL, and
// switches replace the history entry instead of pushing one.
export function useTabParam<T extends string>(
  tabs: readonly T[],
  defaultTab: T,
  { param = "tab", aliases, isAvailable }: UseTabParamOptions<T> = {},
): [T, (tab: string) => void] {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const raw = searchParams.get(param);
  const resolved = raw !== null && aliases && Object.hasOwn(aliases, raw) ? aliases[raw] : raw;
  const activeTab =
    tabs.includes(resolved as T) && (isAvailable?.(resolved as T) ?? true)
      ? (resolved as T)
      : defaultTab;

  const setActiveTab = useCallback(
    (tab: string) => {
      // Build from the live URL, not useSearchParams' render-time snapshot:
      // react-router's functional setSearchParams gets the params of the last
      // render, so two tab params set in one event (the flow editor's ?tab= and
      // ?visibility=) would overwrite each other. Relies on <BrowserRouter>,
      // whose navigate updates window.location at once.
      const next = new URLSearchParams(window.location.search);
      if (tab === defaultTab) next.delete(param);
      else next.set(param, tab);
      navigate({ search: next.toString() }, { replace: true });
    },
    [navigate, param, defaultTab],
  );

  return [activeTab, setActiveTab];
}
